import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ThreadLista, VersioneShortlist } from '../api/types';
import { displayBadgeStatoShortlist, nice_date } from '../utils';

interface Props {
  versione: VersioneShortlist;
  precedente?: VersioneShortlist;
  threadsByProblemId: ReadonlyMap<number, ThreadLista>;
}

const parseOrder = (order: string) => order.split(',').filter(value => value.trim() !== '').map(Number);

const VersioneShortlistFeed = ({ versione, precedente, threadsByProblemId }: Props) => {
  const id = useId();
  const [expanded, setExpanded] = useState(false);
  const current = new Set(versione.lista_problemi);
  const previous = new Set(precedente?.lista_problemi || []);
  const added = [...current].filter(problem => !previous.has(problem));
  const removed = [...previous].filter(problem => !current.has(problem));
  const order = parseOrder(versione.ordine);
  const previousOrder = parseOrder(precedente?.ordine || '');
  const commonOrder = order.filter(thread => previousOrder.includes(thread));
  const oldCommonOrder = previousOrder.filter(thread => order.includes(thread));
  const reordered = !!precedente && commonOrder.some((thread, index) => thread !== oldCommonOrder[index]);
  const statusChanged = !!precedente && versione.stato !== precedente.stato;
  const position = new Map(order.map((thread, index) => [thread, index]));
  const fullList = [...current].sort((first, second) => {
    const firstThread = threadsByProblemId.get(first);
    const secondThread = threadsByProblemId.get(second);
    return (firstThread ? position.get(firstThread.id) ?? Infinity : Infinity)
      - (secondThread ? position.get(secondThread.id) ?? Infinity : Infinity);
  });

  const problemLink = (problem: number) => {
    const thread = threadsByProblemId.get(problem);
    return thread ? <Link to={`/thread/problema/${thread.id}/`}>#{thread.id} — {thread.titolo}</Link>
      : <span className="text-muted">Problema non disponibile (ID problema: {problem})</span>;
  };
  const changes = (problems: number[], label: string, kind: 'added' | 'removed') => problems.length > 0 && (
    <div className={`shortlist-version-change shortlist-version-${kind}`}>
      <span className="shortlist-version-change-label">{label} ({problems.length})</span>
      <ul>{problems.map(problem => <li key={problem}>{problemLink(problem)}</li>)}</ul>
    </div>
  );

  return <article className="shortlist-version-event" aria-labelledby={`${id}-heading`}>
    <div className="shortlist-version-heading">
      <h3 id={`${id}-heading`}><i className={`fa ${precedente ? 'fa-check-square' : 'fa-plus-circle'} mr-2`} aria-hidden="true" />
        {precedente ? 'Shortlist aggiornata' : 'Shortlist creata'}
      </h3>
      <span className="small text-muted">Versione #{versione.num} · <time dateTime={versione.data}>{nice_date(versione.data)}</time></span>
    </div>
    <div className="shortlist-version-summary-row">
      <div className="shortlist-version-summary">
        {!precedente ? <>
          <p className="small mb-2">{current.size} {current.size === 1 ? 'problema nella versione iniziale' : 'problemi nella versione iniziale'}.</p>
          <p className="shortlist-version-status">Stato iniziale: {displayBadgeStatoShortlist(versione.stato)}</p>
        </> : <>
          {changes(added, 'Aggiunti', 'added')}
          {changes(removed, 'Rimossi', 'removed')}
          {reordered && <p className="small mb-2">Ordine di preferenza modificato.</p>}
          {statusChanged && <p className="shortlist-version-status">Stato: {displayBadgeStatoShortlist(precedente.stato)}
            <span aria-label="diventa"> → </span>{displayBadgeStatoShortlist(versione.stato)}</p>}
          {!added.length && !removed.length && !reordered && !statusChanged &&
            <p className="small text-muted mb-2">Nessuna modifica a problemi, ordine o stato.</p>}
        </>}
      </div>
      <button type="button" className="btn btn-sm btn-outline-secondary shortlist-version-toggle" aria-expanded={expanded} aria-controls={`${id}-problems`}
        onClick={() => setExpanded(value => !value)}>{expanded ? 'Nascondi elenco completo' : 'Mostra elenco completo'} ({current.size})</button>
    </div>
    {expanded && <div id={`${id}-problems`} className="shortlist-version-full-list">
      {fullList.length ? <ol>{fullList.map(problem => <li key={problem}>{problemLink(problem)}</li>)}</ol>
        : <p className="small text-muted mb-0">Nessun problema in questa versione.</p>}
    </div>}
  </article>;
};

export default VersioneShortlistFeed;
